import React, {ChangeEvent, useMemo, useState} from 'react';
import Field from "../Field.tsx";
import {SeriesInfo} from "../../models/interfaces/series.interface.ts";
import SmartIcon from "../SmartIcon.tsx";
import {SeriesModalAction} from "../../reducers/seriesModalReducer.ts";
import LanguageInput from "../CreateProjectModal/LanguageInput.tsx";
import useSystemStore from "../../store/useSystemStore.ts";
import iso6391 from "iso-639-1";

interface SeriesModalGeneralTabProps {
  seriesInfo: SeriesInfo;
  dispatch: React.ActionDispatch<[action: SeriesModalAction]>
}

const SeriesModalGeneralTab: React.FC<SeriesModalGeneralTabProps> = (
  {
    seriesInfo,
    dispatch
  }: SeriesModalGeneralTabProps) => {
  const {supportedCodeLanguages} = useSystemStore()
  const [languageInputStates, setLanguageInputStates] = useState<Record<string, {
    isOpen: boolean;
    inputValue: string;
  }>>(() => {
    const initial: Record<string, { isOpen: boolean; inputValue: string }> = {};
    for (const code of Object.keys(seriesInfo.nameTranslations)) {
      initial[code] = {
        isOpen: false,
        inputValue: iso6391.getName(code),
      };
    }
    return initial;
  });

  const languagesName: string[] = useMemo(() => {
    return supportedCodeLanguages.map((langCode: string) => iso6391.getName(langCode));
  }, [supportedCodeLanguages]);

  const addNameTranslation = () => {
    dispatch({
      type: "ADD_NEW_NAME_TRANSLATION",
    });
  };

  const updateLanguageInputState = (code: string, changes: Partial<{ isOpen: boolean; inputValue: string }>) => {
    setLanguageInputStates(prev => ({
      ...prev,
      [code]: {
        ...prev[code],
        ...changes,
      },
    }));
  };

  const handleLanguageSelection = (oldCode: string, newLangName: string) => {
    const newCode = iso6391.getCode(newLangName);

    // 1. Update the reducer
    dispatch({
      type: "SET_NAME_TRANSLATION_KEY",
      payload: {oldValue: oldCode, newValue: newCode}
    });

    // 2. Copy over existing input state to the new key
    setLanguageInputStates((prev) => {
      const updated = {...prev};
      updated[newCode] = {
        isOpen: false,
        inputValue: newLangName,
      };
      delete updated[oldCode];
      return updated;
    });
  };

  const editNameTranslationValue = (languageCode: string, translation: string) => {
    dispatch({
      type: "SET_NAME_TRANSLATION_VALUE",
      payload: {languageCode, translation}
    });
  };

  const removeNameTranslation = (languageCode: string) => {
    dispatch({
      type: "REMOVE_NAME_TRANSLATION",
      payload: {languageCode}
    });
  };

  return (
    <div>
      <div className="mb-6 h4">General</div>
      <Field
        className="w-1/2"
        classInput="cursor-not-allowed"
        label="Source Language"
        value={iso6391.getName(seriesInfo.sourceLanguage)}
        onChange={() => null}
        disabled
      />
      <div className="flex flex-col">
        <div className="flex items-center justify-between w-full">
          <div className="my-6 h6">Name Translation</div>
          <div
            className="p-2 text-sm border border-n-400 rounded-xl hover:border-brandPurple-500 hover:text-brandPurple-500 cursor-pointer active:scale-95 transition-transform"
            onClick={addNameTranslation}
          >
            + Add Translation
          </div>
        </div>
        <div className="relative pointer-events-auto">
          <div className="max-h-[350px] min-h-[250px] overflow-y-auto pl-2 pr-4">
            {Object.keys(seriesInfo.nameTranslations).map((languageCode: string, index: number) => (
              <div
                key={languageCode}
                className={`flex items-center justify-between w-full gap-4 ${index !== 0 && "mt-4"}`}
              >
                <div className="w-full">
                  <LanguageInput
                    isOpen={languageInputStates?.[languageCode]?.isOpen ?? false}
                    setIsOpen={(isOpen) => updateLanguageInputState(languageCode, {isOpen})}
                    languages={languagesName}
                    selectedLanguage={languageInputStates?.[languageCode]?.inputValue ?? ""}
                    onSelectLanguage={(lang: string) => {
                      handleLanguageSelection(languageCode, lang);
                    }}
                    onChange={(value) => updateLanguageInputState(languageCode, {inputValue: value})}
                    placeholder="Select language"
                    label="Language"
                  />
                </div>
                <Field
                  className="w-full"
                  label="Translation"
                  placeholder={`Translation# ${index + 1}`}
                  value={seriesInfo.nameTranslations[languageCode]}
                  onChange={(event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => editNameTranslationValue(languageCode, event.target.value)}
                />
                <div className="mt-6 cursor-pointer" onClick={() => removeNameTranslation(languageCode)}>
                  <SmartIcon name="trash" className="fill-bloodCrimson-400/80 hover:fill-bloodCrimson-400"/>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default SeriesModalGeneralTab;